import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { soulApi } from "../../data/api/SoulApi";
import { stripHtmlTags } from "../../utils/StringTool";

const YES_SUMMARY_VALUE = "Si";
const NO_SUMMARY_VALUE = "No";
const VENEZUELAN_MOBILE_PREFIXES = ["424", "414", "416", "426", "412", "422"];

type FormioSummaryField = {
  key: string;
  label: string;
  type: string;
  values?: any[];
  data?: { values?: any[] };
};

type SummaryItem = {
  id: string;
  label: string;
  value: string;
};

interface FormSummaryModalProps {
  open: boolean;
  onClose: () => void;
  schema: any;
  submission: any;
  submissionId: string;
}

const normalizeEnyes = (value: string) =>
  value.replace(/\u00f1/g, "n").replace(/\u00d1/g, "N");

const normalizeTextValue = (value: unknown) =>
  normalizeEnyes(stripHtmlTags(String(value)));

const normalizeSummaryKey = (value: string) =>
  normalizeTextValue(value)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[_-]+/g, " ")
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, "-")
    .toUpperCase();

const normalizeComparableValue = (value: unknown) =>
  String(value)
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const isPhoneField = (field: FormioSummaryField) => {
  const searchableText = normalizeComparableValue(`${field.key} ${field.label}`);

  return ["telefono", "phone", "celular", "mobile", "whatsapp"].some((term) =>
    searchableText.includes(term)
  );
};

const isEmptySubmissionValue = (value: unknown) =>
  value === undefined ||
  value === null ||
  value === "" ||
  (Array.isArray(value) && value.length === 0);

const getBooleanSummaryValue = (value: unknown, allowNumeric = false) => {
  if (typeof value === "boolean") {
    return value ? YES_SUMMARY_VALUE : NO_SUMMARY_VALUE;
  }

  if (
    allowNumeric &&
    typeof value === "number" &&
    (value === 1 || value === 0)
  ) {
    return value === 1 ? YES_SUMMARY_VALUE : NO_SUMMARY_VALUE;
  }

  if (typeof value !== "string") return null;

  const normalizedValue = normalizeComparableValue(value);
  if (
    ["true", "yes", "si", ...(allowNumeric ? ["1"] : [])].includes(
      normalizedValue
    )
  ) {
    return YES_SUMMARY_VALUE;
  }
  if (
    ["false", "no", ...(allowNumeric ? ["0"] : [])].includes(normalizedValue)
  ) {
    return NO_SUMMARY_VALUE;
  }

  return null;
};

const getOptionLabel = (field: FormioSummaryField, value: unknown) => {
  const options = field.values ?? field.data?.values ?? [];
  const normalizedValue = normalizeComparableValue(value);
  const option = options.find(
    (item) => normalizeComparableValue(item?.value ?? item) === normalizedValue
  );

  return option?.label ?? option?.value ?? null;
};

const formatPhoneValue = (field: FormioSummaryField, value: unknown) => {
  if (!isPhoneField(field)) return value;
  if (typeof value !== "string" && typeof value !== "number") return value;

  const digits = String(value).replace(/\D/g, "");
  const startsWithMobilePrefix = VENEZUELAN_MOBILE_PREFIXES.some((prefix) =>
    digits.startsWith(prefix)
  );

  if ((digits.length === 9 || digits.length === 10) && startsWithMobilePrefix) {
    return `0${digits}`;
  }

  return value;
};

const formatObjectKey = (key: string) =>
  normalizeEnyes(
    key
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/[_-]+/g, " ")
      .trim()
  );

const formatNestedValue = (value: any): string | null => {
  if (isEmptySubmissionValue(value)) return null;

  const booleanValue = getBooleanSummaryValue(value, true);
  if (booleanValue) return booleanValue;

  if (typeof value === "object") return formatObjectValue(value);

  return normalizeTextValue(value);
};

const formatObjectValue = (value: any): string | null => {
  if (Array.isArray(value)) {
    const hasObjectItems = value.some(
      (item) => typeof item === "object" && item !== null
    );
    const formattedItems = value
      .map((item, index) => {
        const formattedItem =
          item?.label !== undefined
            ? formatNestedValue(item.label)
            : item?.value !== undefined
              ? formatNestedValue(item.value)
              : formatNestedValue(item);

        if (!formattedItem) return null;

        return typeof item === "object"
          ? `${index + 1}. ${formattedItem}`
          : formattedItem;
      })
      .filter(Boolean);

    return formattedItems.length
      ? formattedItems.join(hasObjectItems ? "\n" : ", ")
      : null;
  }

  if (value.label !== undefined) return formatNestedValue(value.label);
  if (value.value !== undefined) return formatNestedValue(value.value);

  const formattedEntries = Object.entries(value)
    .map(([key, entryValue]) => {
      const formattedValue = formatNestedValue(entryValue);
      if (!formattedValue) return null;

      return `${formatObjectKey(key)}: ${formattedValue}`;
    })
    .filter(Boolean);

  return formattedEntries.length ? formattedEntries.join("; ") : null;
};

const formatChoiceValue = (field: FormioSummaryField, value: any) => {
  const booleanValue = getBooleanSummaryValue(value, field.type === "checkbox");
  if (booleanValue) return booleanValue;

  if (Array.isArray(value)) {
    return value
      .map(
        (item) =>
          getOptionLabel(field, item) ??
          (item?.label !== undefined ? formatNestedValue(item.label) : null) ??
          (item?.value !== undefined ? formatNestedValue(item.value) : null) ??
          formatNestedValue(item)
      )
      .filter(Boolean)
      .join(", ");
  }

  if (typeof value === "object") {
    const selectedOptions = Object.entries(value)
      .filter(
        ([, selected]) =>
          getBooleanSummaryValue(selected, true) === YES_SUMMARY_VALUE
      )
      .map(([key]) => getOptionLabel(field, key) ?? key);

    if (selectedOptions.length) return selectedOptions.join(", ");
    if (Object.keys(value).length === 0) return null;

    return formatObjectValue(value);
  }

  return getOptionLabel(field, value) ?? value;
};

const getDisplayLabel = (
  key: string,
  fieldLookup: Map<string, FormioSummaryField>
) => {
  const fieldLabel = fieldLookup.get(key)?.label;

  return fieldLabel ? stripHtmlTags(fieldLabel) : formatObjectKey(key);
};

const formatDateValue = (value: any) => {
  try {
    const dateObj = new Date(value);

    return dateObj.toLocaleDateString("es-CO", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  } catch {
    return value;
  }
};

const formatLeafValue = (
  value: any,
  field?: FormioSummaryField
): string | null => {
  if (isEmptySubmissionValue(value)) return null;

  let formattedValue = field ? formatPhoneValue(field, value) : value;

  if (field?.type === "datetime") {
    formattedValue = formatDateValue(formattedValue);
  }

  if (field?.type === "checkbox" || field?.type === "radio") {
    formattedValue = formatChoiceValue(field, formattedValue);
  }

  if (field?.type === "select") {
    const optionLabel = getOptionLabel(field, formattedValue);
    if (optionLabel) formattedValue = optionLabel;
  }

  const booleanValue = getBooleanSummaryValue(formattedValue, true);
  if (booleanValue) return booleanValue;

  if (typeof formattedValue === "object") {
    if (formattedValue?.label !== undefined) {
      return formatLeafValue(formattedValue.label);
    }
    if (formattedValue?.value !== undefined) {
      return formatLeafValue(formattedValue.value);
    }

    return formatObjectValue(formattedValue);
  }

  return normalizeTextValue(formattedValue);
};

const createSummaryItem = (
  id: string,
  label: string,
  value: any,
  field?: FormioSummaryField
): SummaryItem[] => {
  const formattedValue = formatLeafValue(value, field);
  if (!formattedValue) return [];

  return [
    {
      id,
      label: normalizeSummaryKey(label),
      value: formattedValue,
    },
  ];
};

const buildSummaryItems = (
  label: string,
  value: any,
  idPrefix: string,
  fieldLookup: Map<string, FormioSummaryField>,
  field?: FormioSummaryField
): SummaryItem[] => {
  if (isEmptySubmissionValue(value)) return [];

  if (field?.type === "checkbox" || field?.type === "radio") {
    return createSummaryItem(idPrefix, label, value, field);
  }

  if (Array.isArray(value)) {
    return value.flatMap((item, index) => {
      const itemId = `${idPrefix}.${index}`;

      if (typeof item === "object" && item !== null) {
        if (item.label !== undefined || item.value !== undefined) {
          return createSummaryItem(itemId, label, item, field);
        }

        return Object.entries(item).flatMap(([key, entryValue]) => {
          const nestedField = fieldLookup.get(key);
          const nestedLabel = getDisplayLabel(key, fieldLookup);

          return buildSummaryItems(
            nestedLabel,
            entryValue,
            `${itemId}.${key}`,
            fieldLookup,
            nestedField
          );
        });
      }

      return createSummaryItem(itemId, label, item, field);
    });
  }

  if (typeof value === "object" && value !== null) {
    if (value.label !== undefined || value.value !== undefined) {
      return createSummaryItem(idPrefix, label, value, field);
    }

    if (field?.type === "select") {
      return createSummaryItem(idPrefix, label, value, field);
    }

    return Object.entries(value).flatMap(([key, entryValue]) => {
      const nestedField = fieldLookup.get(key);
      const nestedLabel = getDisplayLabel(key, fieldLookup);

      return buildSummaryItems(
        nestedLabel,
        entryValue,
        `${idPrefix}.${key}`,
        fieldLookup,
        nestedField
      );
    });
  }

  return createSummaryItem(idPrefix, label, value, field);
};

function getAllFormioFields(
  components: any[],
  fields: FormioSummaryField[] = []
) {
  components.forEach((comp) => {
    if (comp.input && comp.key && comp.label) {
      fields.push({
        key: comp.key,
        label: comp.label,
        type: comp.type,
        values: comp.values,
        data: comp.data,
      });
    }
    if (comp.components) getAllFormioFields(comp.components, fields);
    if (comp.columns) {
      comp.columns.forEach((col: any) => {
        if (col.components) getAllFormioFields(col.components, fields);
      });
    }
  });
  return fields;
}

export const FormSummaryModal: React.FC<FormSummaryModalProps> = ({
  open,
  onClose,
  schema,
  submission,
  submissionId,
}) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingEdit, setLoadingEdited] = useState(false);
  const [summaryList, setSummaryList] = useState<SummaryItem[]>([]);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const requestNewEditing = async () => {
    setLoadingEdited(true);
    try {
      await soulApi.updateSubmissionToRequestRevision.request({
        submissionId: submissionId,
      });
      setSnackbar({
        open: true,
        message: "Solicitud de revisión enviada correctamente.",
        severity: "success",
      });
    } catch (e: any) {
      console.error("❌ Error solicitando revisión:", e);
      setSnackbar({
        open: true,
        message:
          e?.response?.data?.message ||
          "Ocurrió un error al solicitar la revisión.",
        severity: "error",
      });
    } finally {
      setLoadingEdited(false);
    }
  };

  useEffect(() => {
    if (!open) return;

    setLoading(true);
    setTimeout(() => {
      const fields = getAllFormioFields(schema.components || []);
      const fieldLookup = new Map(fields.map((field) => [field.key, field]));
      const list = fields.flatMap((field) => {
        const rawValue = submission[field.key];
        if (isEmptySubmissionValue(rawValue)) return [];

        const label = stripHtmlTags(field.label);

        return buildSummaryItems(
          label,
          rawValue,
          field.key,
          fieldLookup,
          field
        );
      });

      setSummaryList(list);
      setLoading(false);
    }, 100); // Simula carga pequeña (para UI fluida)
  }, [open, schema, submission]);

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(id);
    setTimeout(() => setCopiedField(null), 1200);
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        onClose();
      }}
      fullScreen
    >
      <DialogTitle
        sx={{
          position: "sticky",
          top: 0,
          background: "white",
          zIndex: 10,
          fontWeight: 700,
          pr: 6,
          borderBottom: "1px solid #eee",
        }}
      >
        Resumen del Formulario
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 16, top: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ minHeight: "60vh" }}
          >
            <CircularProgress color="primary" />
            <Typography sx={{ mt: 2, color: "text.secondary" }}>
              Cargando formulario...
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            <Button
              variant={"outlined"}
              color={"secondary"}
              size="small"
              startIcon={<ContentCopyIcon fontSize="small" />}
              onClick={() => requestNewEditing()}
              sx={{ textTransform: "none", minWidth: 90 }}
              loading={loadingEdit}
            >
              {"Solicitar revisión"}
            </Button>
            {summaryList.map((item) => (
              <Stack
                key={item.id}
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={1.5}
              >
                <Typography
                  sx={{
                    minWidth: { xs: "100%", sm: 260 },
                    fontWeight: 600,
                    wordBreak: "break-word",
                  }}
                >
                  {item.label}:
                </Typography>

                <TextField
                  value={item.value}
                  variant="outlined"
                  size="small"
                  fullWidth
                  multiline={item.value.includes("\n")}
                  minRows={item.value.includes("\n") ? 2 : undefined}
                  maxRows={6}
                  InputProps={{ readOnly: true }}
                  sx={{
                    bgcolor: "#fafafa",
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#ddd" },
                      "&:hover fieldset": { borderColor: "#bbb" },
                    },
                  }}
                />

                <Button
                  variant={
                    copiedField === item.id ? "contained" : "outlined"
                  }
                  color={copiedField === item.id ? "success" : "secondary"}
                  size="small"
                  startIcon={<ContentCopyIcon fontSize="small" />}
                  onClick={() => handleCopy(item.value, item.id)}
                  sx={{ textTransform: "none", minWidth: 90 }}
                >
                  {copiedField === item.id ? "¡Copiado!" : "Copiar"}
                </Button>
              </Stack>
            ))}
          </Stack>
        )}
      </DialogContent>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};
